import { ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { When } from "react-if";
import { DateTime } from "luxon";
import {
  ITeamActivityCounts,
  ITeamActivityResponse,
  ITeamPortfolio,
  TeamActivityPeriodLabels,
} from "shared";

import { Select } from "../../../common/Atoms/Select";
import { classNames } from "../../../common/lib/classNames";
import { formatAmount } from "../../../common/lib/helpers";
import { useFetchTeamActivityQuery } from "../../services/api/teamActivityApi/teamActivity";
import {
  formatCount,
  IDelta,
  percentDelta,
  pointsDelta,
} from "../EmailPerformance/stats";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";

import {
  cardClass,
  CardHeading,
  deltaTone,
  ErrorState,
  FollowUpsCard,
  LegendItem,
  OutcomeBars,
  PeriodSelect,
  SegmentedToggle,
  toneOnDark,
  toneOnLight,
  TypeLegend,
  UserLink,
  userColor,
  usePeriod,
  userPath,
  WeeklyChart,
} from "./common";
import {
  activities,
  countDelta,
  dateRangeLabel,
  deltaText,
  formatTalkTime,
  inEscalationRate,
  keptRate,
  pct,
  reachedRate,
  sparkPath,
  weekActivities,
} from "./stats";

const thClass = `px-3 py-2.5 font-semibold`;
const numClass = `px-3 py-3 text-right tabular-nums`;

interface IHero {
  label: string;
  value: string;
  series: number[];
  delta: IDelta | null;
  extra: string;
}

function Hero({ hero }: { hero: IHero }) {
  const spark = sparkPath(hero.series, 200, 40);
  const tone = deltaTone(hero.delta, `up`);
  const detail = [deltaText(hero.delta), hero.extra].filter(Boolean).join(` · `);

  return (
    <div className={ `flex flex-col gap-1` }>
      <div className={ `text-[13px] text-paidniceGreen-300` }>{ hero.label }</div>
      <div className={ `flex flex-wrap items-end gap-x-4 gap-y-1` }>
        <span className={ `text-3xl font-bold leading-none tabular-nums text-white sm:text-[34px]` }>{ hero.value }</span>
        <When condition={ !!spark }>
          <svg width={ 130 }
            height={ 36 }
            viewBox={ `0 0 200 40` }
            preserveAspectRatio={ `none` }
            className={ `hidden overflow-visible sm:block` }
            aria-hidden>
            <path d={ spark?.area }
              fill={ `rgba(138,177,199,.18)` } />
            <path d={ spark?.line }
              fill={ `none` }
              stroke={ `#8ab1c7` }
              strokeWidth={ 2 } />
            <circle cx={ spark?.last.x }
              cy={ spark?.last.y }
              r={ 3 }
              fill={ `#8ab1c7` } />
          </svg>
        </When>
      </div>
      <span className={ classNames(`text-xs font-semibold`, toneOnDark[tone]) }>{ detail }</span>
    </div>
  );
}

interface ITile {
  label: string;
  value: string;
  delta: IDelta | null;
  goodWhen: `up` | `down` | null;
  danger?: boolean;
}

function buildHeroes(data: ITeamActivityResponse): IHero[] {
  const { totals: t, previousTotals: p, weeks, homeCurrency } = data;
  const calls = t.phoneCalls;
  const reached = t.callOutcomes.spoke_to_customer ?? 0;

  return [
    {
      label: `Activities logged`,
      value: formatCount(activities(t)),
      series: weeks.map(weekActivities),
      delta: percentDelta(activities(t), activities(p)),
      extra: inEscalationRate(t) === null ? `` : `${pct(inEscalationRate(t))} inside escalations`,
    },
    {
      label: `Calls where the customer was reached`,
      value: pct(reachedRate(t)),
      series: weeks.map(w => (w.phoneCalls > 0 ? (w.reached / w.phoneCalls) * 100 : 0)),
      delta: pointsDelta(reachedRate(t), reachedRate(p)),
      extra: `${formatCount(reached)} of ${formatCount(calls)} calls`,
    },
    {
      label: `Promised to pay on calls`,
      value: formatAmount(t.promisedHomeCents, homeCurrency),
      series: weeks.map(w => w.promisedHomeCents),
      delta: percentDelta(t.promisedHomeCents, p.promisedHomeCents),
      extra: `${formatCount(t.promises)} promises`,
    },
  ];
}

function buildTiles(data: ITeamActivityResponse): ITile[] {
  const { totals: t, previousTotals: p } = data;

  return [
    { label: `Phone calls`, value: formatCount(t.phoneCalls), delta: percentDelta(t.phoneCalls, p.phoneCalls), goodWhen: `up` },
    { label: `Talk time`, value: formatTalkTime(t.talkTimeSeconds), delta: percentDelta(t.talkTimeSeconds, p.talkTimeSeconds), goodWhen: `up` },
    { label: `Text messages`, value: formatCount(t.textMessages), delta: percentDelta(t.textMessages, p.textMessages), goodWhen: null },
    { label: `External emails`, value: formatCount(t.externalEmails), delta: percentDelta(t.externalEmails, p.externalEmails), goodWhen: null },
    { label: `Promises kept`, value: pct(keptRate(t)), delta: pointsDelta(keptRate(t), keptRate(p)), goodWhen: `up` },
    {
      label: `Escalations past scheduled`,
      value: formatCount(data.escalations.pastScheduled),
      delta: countDelta(data.escalations.pastScheduled, data.previousPastScheduled),
      goodWhen: `down`,
      danger: true,
    },
  ];
}

function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <th className={ classNames(thClass, right ? `text-right` : `text-left`) }>{ children }</th>;
}

function TeamTable({ data }: { data: ITeamActivityResponse }) {
  const teamReach = reachedRate(data.totals);
  const teamKept = keptRate(data.totals);

  // A rate is flagged when it is more than 5 points under the team's
  const below = (value: number | null, team: number | null) => value !== null && team !== null && value < team - 5;

  return (
    <div className={ `${cardClass} lg:col-span-2` }>
      <CardHeading title={ `Team` }
        subtitle={ `All activity, inside and outside escalations` } />
      <div className={ `mt-3 overflow-x-auto` }>
        <table className={ `w-full border-collapse text-sm` }>
          <thead>
            <tr className={ `border-b border-gray-300` }>
              <Th>{ `User` }</Th>
              <Th right>{ `Calls` }</Th>
              <Th right>{ `Reached` }</Th>
              <Th right>{ `Texts` }</Th>
              <Th right>{ `Emails` }</Th>
              <Th right>{ `In escalations` }</Th>
              <Th right>{ `Promised` }</Th>
              <Th right>{ `Kept` }</Th>
            </tr>
          </thead>
          <tbody>
            { data.users.map(u => {
              const reach = reachedRate(u.counts);
              const kept = keptRate(u.counts);

              return (
                <tr key={ u.id }
                  className={ `border-b border-gray-200` }>
                  <td className={ `px-3 py-3 text-gray-900` }><UserLink user={ u } /></td>
                  <td className={ `${numClass} text-gray-700` }>{ formatCount(u.counts.phoneCalls) }</td>
                  <td className={ classNames(numClass, below(reach, teamReach) ? `font-semibold text-paidniceRed-700` : `text-gray-700`) }>{ pct(reach) }</td>
                  <td className={ `${numClass} text-gray-700` }>{ formatCount(u.counts.textMessages) }</td>
                  <td className={ `${numClass} text-gray-700` }>{ formatCount(u.counts.externalEmails) }</td>
                  <td className={ `${numClass} text-gray-700` }>{ pct(inEscalationRate(u.counts)) }</td>
                  <td className={ `${numClass} font-medium text-gray-900` }>{ formatAmount(u.counts.promisedHomeCents, data.homeCurrency) }</td>
                  <td className={ classNames(numClass, below(kept, teamKept) ? `text-amber-700` : `text-gray-700`) }>{ pct(kept) }</td>
                </tr>
              );
            }) }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PortfolioTable({ data, periodLabel }: { data: ITeamActivityResponse; periodLabel: string }) {
  const { totalPortfolio: total, homeCurrency } = data;
  const asAt = DateTime.fromISO(data.end).toFormat(`d LLL yyyy`);
  const rows: Array<{ key: string; name: ReactNode; pf: ITeamPortfolio; total?: boolean }> = [
    ...data.users.map(u => ({ key: u.id, name: <UserLink user={ u } />, pf: u.portfolio })),
    { key: `unassigned`, name: `Unassigned`, pf: data.unassignedPortfolio },
    { key: `total`, name: `Total`, pf: total, total: true },
  ];

  return (
    <div className={ cardClass }>
      <CardHeading
        title={ `Assigned contacts and invoices` }
        subtitle={ `Balances as at ${asAt}. An invoice counts toward its assignee, or the contact's assignee if it has none. Includes invoices with no escalation or activity.` }
        action={
          <div className={ `flex shrink-0 gap-3.5` }>
            <LegendItem color={ `#c11415` }
              label={ `Overdue` } />
            <LegendItem color={ `#8ab1c7` }
              label={ `Not yet due` } />
          </div>
        } />
      <div className={ `mt-3 overflow-x-auto` }>
        <table className={ `w-full min-w-[900px] border-collapse text-sm` }>
          <thead>
            <tr className={ `border-b border-gray-200 bg-gray-50` }>
              <Th>{ `User` }</Th>
              <Th right>{ `Contacts` }</Th>
              <Th right>{ `Open invoices` }</Th>
              <Th right>{ `Outstanding` }</Th>
              <th className={ `${thClass} w-[220px] text-left` }>{ `Overdue` }</th>
              <Th right>{ `Avg days overdue` }</Th>
              <Th right>{ `Collected (${periodLabel})` }</Th>
              <Th right>{ `No contact in 30 days` }</Th>
            </tr>
          </thead>
          <tbody>
            { rows.map(({ key, name, pf, total: isTotal }) => {
              const overduePct = pf.outstandingHomeCents > 0 ? (pf.overdueHomeCents / pf.outstandingHomeCents) * 100 : 0;
              // Flagged when well past the team's average, or when a quarter of the contacts have gone quiet
              const slow = !isTotal && pf.avgDaysOverdue !== null && total.avgDaysOverdue !== null && pf.avgDaysOverdue > total.avgDaysOverdue + 5;
              const idle = !isTotal && pf.contacts > 0 && pf.noContactIn30Days / pf.contacts >= 0.25;

              return (
                <tr key={ key }
                  className={ classNames(`border-b border-gray-200`, isTotal && `bg-gray-50`) }>
                  <td className={ classNames(`px-3 py-3 text-gray-900`, isTotal ? `font-semibold` : `font-medium`) }>{ name }</td>
                  <td className={ `${numClass} text-gray-700` }>{ formatCount(pf.contacts) }</td>
                  <td className={ `${numClass} text-gray-700` }>{ formatCount(pf.openInvoices) }</td>
                  <td className={ `${numClass} font-medium text-gray-900` }>{ formatAmount(pf.outstandingHomeCents, homeCurrency) }</td>
                  <td className={ `px-3 py-3` }>
                    <div className={ `flex items-center gap-2.5` }>
                      <div className={ `flex h-2 flex-1 overflow-hidden rounded-full bg-[#8ab1c7]` }>
                        <div className={ `bg-[#c11415]` }
                          style={ { width: `${overduePct}%` } } />
                      </div>
                      <span className={ `w-[130px] text-right tabular-nums text-gray-900` }>
                        { formatAmount(pf.overdueHomeCents, homeCurrency) }
                        <span className={ `text-gray-400` }>{ ` ${overduePct.toFixed(0)}%` }</span>
                      </span>
                    </div>
                  </td>
                  <td className={ classNames(numClass, slow ? `font-semibold text-paidniceRed-700` : `text-gray-700`) }>
                    { pf.avgDaysOverdue === null ? `-` : `${Math.round(pf.avgDaysOverdue)} days` }
                  </td>
                  <td className={ `${numClass} text-gray-700` }>{ formatAmount(pf.collectedHomeCents, homeCurrency) }</td>
                  <td className={ classNames(numClass, idle ? `font-semibold text-paidniceRed-700` : `text-gray-700`) }>{ formatCount(pf.noContactIn30Days) }</td>
                </tr>
              );
            }) }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function callsSubtitle(c: ITeamActivityCounts) {
  return `${formatCount(c.phoneCalls)} calls, click to call and logged`;
}

export function TeamActivity() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const { period, setPeriod } = usePeriod();
  const [mode, setMode] = useState<`type` | `user`>(`type`);

  const { data, isLoading, isFetching, isError, refetch } = useFetchTeamActivityQuery({ organisationId: organisationId!, period }, {
    skip: !organisationId,
  });

  const openUser = (userId: string) => {
    if (userId !== `all`) {
      navigate(`/${organisationId}${userPath(userId, period)}`);
    }
  };

  const userOptions = [{ label: `All users`, value: `all` }, ...(data?.users ?? []).map(u => ({ label: u.name, value: u.id }))];

  return (
    <LoadingOverlay loading={ isLoading || isFetching }>
      <div className={ `mx-auto flex max-w-[1400px] flex-col gap-5` }>
        <div className={ `overflow-hidden rounded-xl bg-white ring-1 ring-black/5` }>
          <div className={ `flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-[#2F4E60] px-4 pb-4 pt-4 sm:px-7 sm:pb-5 sm:pt-5` }>
            <div className={ `flex flex-wrap items-baseline gap-x-3 gap-y-0.5` }>
              <span className={ `text-[15px] font-bold text-white sm:text-[19px]` }>{ `Team Activity` }</span>
              <When condition={ !!data }>
                <span className={ `text-[13px] text-paidniceGreen-300` }>{ data && dateRangeLabel(data.start, data.end) }</span>
              </When>
            </div>
            <div className={ `flex flex-wrap items-center gap-2` }>
              <PeriodSelect period={ period }
                onChange={ setPeriod } />
              <div className={ `w-44` }>
                <Select
                  options={ userOptions }
                  selected={ `all` }
                  onChange={ ({ value }) => openUser(String(value)) }
                  size={ `sm` }
                />
              </div>
            </div>
          </div>

          <When condition={ !!data }>
            { data && (
              <>
                <div className={ `bg-[#2F4E60] px-4 pb-5 sm:px-7 sm:pb-6` }>
                  <div className={ `flex flex-col gap-4 sm:flex-row sm:gap-9` }>
                    { buildHeroes(data).map((hero, i) => (
                      <div key={ hero.label }
                        className={ i > 0 ? `sm:border-l sm:border-paidniceGreen-700 sm:pl-9` : `` }>
                        <Hero hero={ hero } />
                      </div>
                    )) }
                  </div>
                </div>

                <div className={ `grid grid-cols-2 gap-px bg-gray-100 sm:grid-cols-3 lg:grid-cols-6` }>
                  { buildTiles(data).map(tile => (
                    <div key={ tile.label }
                      className={ `flex flex-col gap-1 bg-white px-4 pb-2.5 pt-3` }>
                      <div className={ `min-h-8 text-[11.5px] text-gray-500` }>{ tile.label }</div>
                      <div className={ `flex items-baseline justify-between gap-2` }>
                        <span className={ classNames(
                          `whitespace-nowrap text-[15px] font-bold tabular-nums`,
                          tile.danger ? `text-paidniceRed-700` : `text-paidniceGreen-900`,
                        ) }>
                          { tile.value }
                        </span>
                        <span className={ classNames(`whitespace-nowrap text-[11px] font-semibold`, toneOnLight[deltaTone(tile.delta, tile.goodWhen)]) }>
                          { deltaText(tile.delta) }
                        </span>
                      </div>
                    </div>
                  )) }
                </div>

                <div className={ `border-t border-gray-200 px-4 pb-5 pt-3 sm:px-7` }>
                  <div className={ `mb-3 flex flex-wrap items-center justify-between gap-2` }>
                    <div className={ `flex flex-wrap items-baseline gap-x-3 gap-y-1` }>
                      <span className={ `text-sm font-bold text-paidniceGreen-900` }>{ `Activities per week` }</span>
                      { mode === `type`
                        ? <TypeLegend />
                        : data.users.map((u, i) => (
                          <LegendItem key={ u.id }
                            color={ userColor(i) }
                            label={ u.name } />
                        )) }
                    </div>
                    <SegmentedToggle<`type` | `user`>
                      options={ [{ value: `type`, label: `By type` }, { value: `user`, label: `By user` }] }
                      value={ mode }
                      onChange={ setMode } />
                  </div>
                  <WeeklyChart weeks={ data.weeks }
                    mode={ mode }
                    users={ data.users } />
                </div>
              </>
            ) }
          </When>
        </div>

        <When condition={ isError && !isFetching }>
          <ErrorState what={ `your team activity` }
            onRetry={ refetch } />
        </When>

        <When condition={ !!data }>
          { data && (
            <>
              <div className={ `grid items-start gap-5 lg:grid-cols-3` }>
                <TeamTable data={ data } />
                <div className={ `${cardClass} flex flex-col gap-3` }>
                  <CardHeading title={ `Call outcomes` }
                    subtitle={ callsSubtitle(data.totals) } />
                  <OutcomeBars counts={ data.totals } />
                </div>
              </div>

              <FollowUpsCard followUps={ data.followUps }
                homeCurrency={ data.homeCurrency }
                showUser />

              <PortfolioTable data={ data }
                periodLabel={ TeamActivityPeriodLabels[period].toLowerCase() } />
            </>
          ) }
        </When>
      </div>
    </LoadingOverlay>
  );
}
